import { Formik } from 'formik';
import React from 'react';
import styled, { css } from 'styled-components';
import { rem } from 'polished';
import { useTranslation } from 'react-i18next';
import { LockShield } from 'icons';
import { MultipleCheckboxes } from 'LoggedOut/Assets/Checkbox/MultipleCheckboxes';
import { small } from 'utils';
import { useLocation } from 'react-router';
import { tracking } from 'App/Tracking';
import { ConsentType } from '__generated__/globalTypes';
import { SEPARATE_CONSENT_INTRO_KEY } from 'LoggedOut/PrivacyConsent/consentRequirements';
import { useConsentsToCollect } from 'LoggedOut/PrivacyConsent/useConsentsToCollect';
import LoadingIndicator from 'Shared/LoadingIndicator';
import {
  LoggedOutScreenTitle,
  LoggedOutScreenWrapper,
} from '../../LoggedOutScreenWrapper';
import { UserDetails } from '../SignUp';
import { TsAndCsStatement } from '../TsAndCsStatement';
import { StyledForm, StyledSubmitButton } from './CommonFormStyledComponents';

const PrivacyPointList = styled.div`
  margin-top: ${rem(24)};
  margin-bottom: ${rem(12)};
`;

const PrivacyInformationHeader = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  font-size: ${rem(20)};
  font-weight: 600;
  margin-top: ${rem(16)};
  margin-bottom: ${rem(12)};
`;

const PrivacyInformationContainer = styled.div`
  border-bottom: ${rem(1)} solid ${({ theme }) => theme.colors.border.secondary};
`;

const PrivacyInformationText = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  text-size: ${rem(16)};
  margin-bottom: ${rem(24)};
`;

const LockShieldIcon = styled(LockShield).attrs(({ theme }) => ({
  primaryColor: theme.colors.text.primary,
  secondaryColor: theme.colors.text.primary,
  height: '20px',
  width: '20px',
}))`
  flex-shrink: 0;
  margin-right: ${rem(16)};
`;

// Sized to match the checkbox labels: the activities below continue this sentence.
const SeparateConsentIntro = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${rem(16)};
  margin-top: ${rem(24)};
`;

const StickyFooter = styled.div`
  position: sticky;
  bottom: 0;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.background.card};
  border-top-left-radius: ${rem(12)};
  border-top-right-radius: ${rem(12)};
  padding: ${rem(12)};

  ${small(css`
    background-color: ${({ theme }) => theme.colors.background.primary};
  `)};
`;

export interface PrivacyConsentFormProps {
  onSubmit(details: UserDetails): void;
  error?: string;
  initialValues?: UserDetails;
  loading?: boolean;
  isInvitedUser?: boolean;
  /** Required by this user's location and not yet given. */
  outstandingConsents: ConsentType[];
  /** Offered on a first consent capture only: the stored value is write-only. */
  showMarketingOptIn?: boolean;
  /** Holds the form back: the set of boxes to ask for is not known yet. */
  loadingConsents?: boolean;
}

const PrivacyConsentForm = ({
  onSubmit,
  loading,
  initialValues = {
    explicitPrivacyConsentGiven: false,
    marketingOptIn: false,
    explicitEEADataStorageConsentGiven: false,
  },
  outstandingConsents,
  showMarketingOptIn = true,
  loadingConsents = false,
}: PrivacyConsentFormProps) => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const isInvitedUser = params.get('invite_token') ? true : false;

  const { t: translate } = useTranslation([
    'logged_out',
    'shared',
    'password_validation',
  ]);

  const {
    checkboxes,
    separateConsentCheckboxes,
    loading: loadingCheckboxes,
  } = useConsentsToCollect({ outstandingConsents, showMarketingOptIn });

  // Nothing renders until the full set is known: the legacy box would show
  // first and then be swapped for the PIPL ones.
  if (loadingConsents || loadingCheckboxes) {
    return <LoadingIndicator />;
  }

  const shownCheckboxes = [...checkboxes, ...separateConsentCheckboxes];

  const missingConsents = (values: UserDetails) =>
    shownCheckboxes
      .filter(({ name, optional }) => !optional && !values[name])
      .map(({ name }) => name);

  const formInitialValues: UserDetails = {
    ...Object.fromEntries(shownCheckboxes.map(({ name }) => [name, false])),
    ...initialValues,
  };

  const legendId = 'privacy-consent-legend';
  const separateConsentLegendId = 'privacy-consent-separate-legend';

  return (
    <LoggedOutScreenWrapper
      customFooter={
        <StickyFooter>
          <TsAndCsStatement />
        </StickyFooter>
      }
      dataTestId="privacy-consent-page"
    >
      <LoggedOutScreenTitle data-testid="form-title" id={legendId}>
        {translate('logged_out:sign_up.forms.privacy_consent.heading')}
      </LoggedOutScreenTitle>
      <PrivacyInformationContainer>
        {!isInvitedUser && (
          <PrivacyInformationHeader>
            <LockShieldIcon />
            {translate(
              'logged_out:explicit_privacy_consent_needed.declaration',
            )}
          </PrivacyInformationHeader>
        )}
        <PrivacyPointList>
          <PrivacyInformationText>
            {translate('logged_out:sign_up.forms.privacy_consent.point_1')}
          </PrivacyInformationText>
          {!isInvitedUser && (
            <PrivacyInformationText>
              {translate('logged_out:sign_up.forms.privacy_consent.point_2')}
            </PrivacyInformationText>
          )}
        </PrivacyPointList>
      </PrivacyInformationContainer>
      <Formik
        validate={values =>
          Object.fromEntries(
            missingConsents(values).map(field => [field, true]),
          )
        }
        initialValues={formInitialValues}
        isInitialValid={missingConsents(formInitialValues).length === 0}
        onSubmit={userDetails => {
          tracking.track('privacy-consent-granted', {
            consentFields: shownCheckboxes
              .map(({ name }) => name)
              .filter(field => userDetails[field]),
          });

          onSubmit(userDetails);
        }}
      >
        {({ isValid }) => (
          <StyledForm>
            <MultipleCheckboxes
              ariaLabelledby={legendId}
              checkboxes={checkboxes}
            />
            {separateConsentCheckboxes.length > 0 ? (
              <>
                {/* The activities below read as continuations of this line. */}
                <SeparateConsentIntro id={separateConsentLegendId}>
                  {translate(SEPARATE_CONSENT_INTRO_KEY)}
                </SeparateConsentIntro>
                <MultipleCheckboxes
                  ariaLabelledby={separateConsentLegendId}
                  checkboxes={separateConsentCheckboxes}
                />
              </>
            ) : null}
            <StyledSubmitButton
              variant="primary"
              data-testid="submit-button"
              type="submit"
              loading={loading}
              label={translate(
                'logged_out:sign_up.forms.privacy_consent.submit_button.label',
              )}
              disabled={!isValid}
            />
          </StyledForm>
        )}
      </Formik>
    </LoggedOutScreenWrapper>
  );
};

export default PrivacyConsentForm;
